/* eslint-disable import/no-anonymous-default-export */

import { useCallback } from 'react';
import { formatListData } from 'commons-ui';
import _empty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { GAME_KEY_MAP } from 'pages/admin/zooKeeper/utils/helper';
import { APP_LANGUAGE } from 'commons/models/common';
import {
	EGameTournamentBanners,
	ITournamentBanner,
	ITournamentBannerContextData,
	ITournamentBannerRow,
	LOBBY_BANNERS_ZK_VALUES,
	TTournamentBannerConfig
} from './constants';
import zkUseService from '../../../admin/zooKeeper/utils/useService';
import { hydrateCreateFormData } from './formDataParser';

const useService = () => {
	const { getKeyValue, updateKeyValue } = zkUseService();

	const formatLobbyBannerListData = (result: any) => {
		const data: any = [];
		if (_empty(result)) {
			return data;
		}
		Object.keys(result).forEach((language: string) => {
			const rows = result[language];
			if (_empty(rows)) {
				return;
			}
			rows.forEach((newRow: any) => {
				const row = newRow;
				row.language = language;
				if (row?.cta) {
					row.ctaType = row.cta?.type;
					row.ctaValue = row.cta?.value;
					if (row.cta?.params) {
						row.ctaParamsGameName = row.cta.params?.gameName;
					}
					delete row.cta;
				}
				data.push(row);
			});
		});

		return data;
	};

	const getEmptyTournamentBannerDataConfig = (): TTournamentBannerConfig => {
		const tournamentBannerDataConfig: TTournamentBannerConfig = {
			[APP_LANGUAGE.ENGLISH]: [],
			[APP_LANGUAGE.HINDI]: []
		};

		return tournamentBannerDataConfig;
	};

	const handleEditConfigBasedLobbyBanner = (
		editedData: ITournamentBanner,
		data: ITournamentBannerRow[],
		language: APP_LANGUAGE,
		index: number
	): TTournamentBannerConfig => {
		const tournamentBannerDataConfig: TTournamentBannerConfig = getEmptyTournamentBannerDataConfig();

		data.forEach((row: ITournamentBannerRow) => {
			if (index === row.index) {
				tournamentBannerDataConfig[language]?.push(editedData);
			} else {
				const banner: ITournamentBanner = hydrateCreateFormData(row);
				tournamentBannerDataConfig[row.language]?.push(banner);
			}
		});

		return tournamentBannerDataConfig;
	};

	const handleDeleteConfigBasedLobbyBanner = (data: ITournamentBannerRow[], index: number): TTournamentBannerConfig => {
		const tournamentBannerDataConfig: TTournamentBannerConfig = getEmptyTournamentBannerDataConfig();

		data.forEach((row: ITournamentBannerRow) => {
			if (index !== row.index) {
				const banner: ITournamentBanner = hydrateCreateFormData(row);
				tournamentBannerDataConfig[row.language]?.push(banner);
			}
		});

		return tournamentBannerDataConfig;
	};

	const handleCreateConfigBasedLobbyBanner = (
		createdData: ITournamentBanner,
		data: ITournamentBannerRow[],
		language: APP_LANGUAGE
	): TTournamentBannerConfig => {
		const tournamentBannerDataConfig: TTournamentBannerConfig = getEmptyTournamentBannerDataConfig();

		data.forEach((row: ITournamentBannerRow) => {
			const banner: ITournamentBanner = hydrateCreateFormData(row);
			tournamentBannerDataConfig[row.language]?.push(banner);
		});

		tournamentBannerDataConfig[language]?.push(createdData);

		return tournamentBannerDataConfig;
	};

	const getTournamentBannersKeyName = (gameName: string): string => {
		const gameNameUpperCase: string = gameName.toUpperCase();
		return (
			EGameTournamentBanners[gameNameUpperCase as keyof typeof EGameTournamentBanners] ||
			LOBBY_BANNERS_ZK_VALUES.keyPrefix + gameNameUpperCase
		);
	};

	const getLobbyBannersData = useCallback(
		async ({ app, gameName, serviceName }: { app: any; gameName: string; serviceName: string }) => {
			const { appName } = app;
			const keyName: string = getTournamentBannersKeyName(gameName);
			const result: {
				value: any;
				success: boolean;
			} = await getKeyValue({ game: GAME_KEY_MAP[appName], keyName, serviceName });

			const data = formatLobbyBannerListData(result?.value);
			const listData = formatListData(data);
			const rowCount = listData.length;
			return { rowCount, data: listData };
		},
		[]
	);

	const requestCreateLobbyBanner = useCallback((appName: string, user, gameName: string, bannerData: TTournamentBannerConfig) => {
		const keyName: string = getTournamentBannersKeyName(gameName);

		const postBannerData = {
			keyName,
			serviceName: LOBBY_BANNERS_ZK_VALUES.serviceName,
			agent: _get(user, 'userName', ''),
			desc: `Updated ${keyName} banner`,
			data: bannerData as any,
			game: GAME_KEY_MAP[appName]
		};
		return updateKeyValue(postBannerData);
	}, []);

	const toggleIsActiveField = useCallback(
		async (rowData: any, lobbyBannerRows: ITournamentBannerContextData, user: any, app: any, showAlert: any) => {
			const data = { ...rowData };
			data.isActive = !data.isActive;
			const { language, index } = data;
			const updatedData: ITournamentBanner = hydrateCreateFormData(data);

			const lobbyBannerDataConfig: TTournamentBannerConfig = handleEditConfigBasedLobbyBanner(
				updatedData,
				lobbyBannerRows.data,
				language,
				index
			);

			try {
				const response = await requestCreateLobbyBanner(app.appName, user, lobbyBannerRows.gameName, lobbyBannerDataConfig);
				if (!response.success) {
					showAlert(response.data.error || 'Error occured while updated', { type: 'error' });
					return;
				}
				// eslint-disable-next-line no-param-reassign
				rowData.isActive = data.isActive;
				showAlert(data.isActive ? 'The banner is deployed now' : 'The banner is removed from the app');
			} catch (error: any) {
				showAlert(error.response.data.error || 'Error occured while updated', { type: 'error' });
			}
		},
		[]
	);

	const deleteLobbyBanner = useCallback(
		async (rowData: any, lobbyBannerRows: ITournamentBannerContextData, user: any, app: any, showAlert: any) => {
			const { index } = rowData;
			const lobbyBannerDataConfig: TTournamentBannerConfig = handleDeleteConfigBasedLobbyBanner(lobbyBannerRows.data, index);

			try {
				const response = await requestCreateLobbyBanner(app.appName, user, lobbyBannerRows.gameName, lobbyBannerDataConfig);
				if (!response.success) {
					showAlert(response.data.error || 'Error occured while delete', { type: 'error' });
					return;
				}
				showAlert('The banner is deleted from the app');
			} catch (error: any) {
				showAlert(error.response.data.error || 'Error occured while updated', { type: 'error' });
			}
		},
		[]
	);

	return {
		handleCreateConfigBasedLobbyBanner,
		handleEditConfigBasedLobbyBanner,
		getLobbyBannersData,
		requestCreateLobbyBanner,
		toggleIsActiveField,
		deleteLobbyBanner
	};
};

export default useService;
